import clsx from "clsx";
import { createRef, FunctionComponent, useState } from "react";

import DangerouslySetCMSMarkup from "@ui/components/article/DangerouslySetCMSMarkup/DangerouslySetCMSMarkup";
import CaretRightIcon from "@ui/components/icon/CaretRightIcon";
import CloseIcon from "@ui/components/icon/CloseIcon";
import DuetLink from "@ui/components/navigation/DuetLink";
import { SELECTORS } from "@ui/config";
import useAnalyticsObserver from "@ui/hooks/use-analytics-observer";
import { sharedStyles } from "@ui/shared-styles";
import { srOnly } from "@ui/shared-styles/accessibility.css";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useCampaignContext } from "@voxmedia/duet-context/context/campaign-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";

import * as styles from "./BreakingNewsBanner.css";

const BreakingNewsBanner: FunctionComponent = () => {
  const { breakingNewsBanner } = useGlobalDataContext();
  const { campaign } = useCampaignContext();
  const analytics = useAnalytics();

  const bannerRef = createRef<HTMLDivElement>();

  const [hidden, setHidden] = useState(false);

  const analyticsName = breakingNewsBanner?.content
    .map((p) => p.html)
    .join(" ");

  useAnalyticsObserver(bannerRef, {
    event: "breaking_news_banner",
    breaking_news_banner: {
      name: analyticsName,
      action: "view",
      position: "breaking_news_banner",
    },
  });

  // Only show Breaking News Banner if there is no campaign
  if (campaign || !breakingNewsBanner) {
    return null;
  }

  const closeBanner = () => {
    setHidden(!hidden);
  };

  const { content, buttonText, buttonUrl } = breakingNewsBanner;
  const normalizedButtonText = buttonText.toLowerCase().replace(/\s/g, "_");

  return (
    <div
      ref={bannerRef}
      className={clsx(
        SELECTORS.BreakingNewsBanner,
        styles.container,
        hidden ? styles.hidden : "",
      )}
    >
      <div
        className={clsx(sharedStyles.layout.mainColumn, styles.innerContainer)}
      >
        <div className={styles.text}>
          <DuetLink
            href={buttonUrl}
            onClick={() =>
              analytics?.emit({
                event: "breaking_news_banner",
                breaking_news_banner: {
                  name: analyticsName,
                  action: `click_${normalizedButtonText}`,
                  position: "breaking_news_banner",
                },
              })
            }
          >
            {content.map((c, i) => (
              <DangerouslySetCMSMarkup
                key={i}
                html={c.html}
                component="span"
                className={styles.descriptionText}
              />
            ))}
            <span className={styles.ctaText}>{buttonText}</span>
            <CaretRightIcon className={styles.caretRight} />
          </DuetLink>
        </div>
        <button onClick={closeBanner}>
          <span className={srOnly}>Close Banner</span>
          <CloseIcon className={styles.closeIcon} />
        </button>
      </div>
    </div>
  );
};

export default BreakingNewsBanner;
